import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Table, Spinner, Form } from "react-bootstrap";
import { Bell, ChevronLeft, ChevronRight, List, Trash } from "react-bootstrap-icons";
import { Link, useLocation } from "react-router-dom";
import { db, collection, getDocs, query, where, doc, updateDoc, deleteDoc } from ".."; // Import necessary Firebase functions
import "../styles/AdminDashboard.css";
import Sidebar from "./SideBar";

const AdminDashboard = () => {
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    tasks: "",
    payments: "",
    messages: "",
  });

  const fetchData = async () => {
    setIsLoading(true);

    const usersRef = collection(db, "users");
    const usersQuery = query(usersRef);
    const userSnapshot = await getDocs(usersQuery);

    const usersDataPromises = userSnapshot.docs.map(async (docData) => {
      const user = docData.data();
      const userId = docData.id;

      // Parallelize payments and projects data fetching
      const [paymentsSnapshot, projectsSnapshot] = await Promise.all([
        getDocs(query(collection(db, "payments"), where("userId", "==", userId))),
        getDocs(query(collection(db, "projects"), where("userId", "==", userId))),
      ]);

      const paymentCount = paymentsSnapshot.size;

      let totalCommunications = 0;
      let totalAssignments = 0;

      projectsSnapshot.docs.forEach((projectDoc) => {
        const project = projectDoc.data();
        const assignments = project.assignments || [];
        totalAssignments += assignments.length;
        assignments.forEach((assignment) => {
          totalCommunications += (assignment.communication_list || []).length;
        });
      });

      return {
        userId: docData.id,
        userName: user.name,
        phone: user.phone,
        totalPayments: paymentCount,
        totalCommunications: totalCommunications,
        totalAssignments: totalAssignments,
        isEnabled: user.isEnabled,
      };
    });

    const usersData = await Promise.all(usersDataPromises);
    setUsers(usersData);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    fetchUnreadNotifications(); // Fetch unread notifications
  }, []); // Only run on mount

  const toggleSidebar = () => {
    if (location.pathname !== "/settings") {
      setShowSidebar(!showSidebar);
    }
  };

  const toggleUserStatus = async (index) => {
    const user = users[index];
    const userRef = doc(db, "users", user.userId);

    const confirmed = window.confirm(
      `Are you sure you want to ${user.isEnabled ? "disable" : "enable"} the user "${user.userName}"?`
    );

    if (confirmed) {
      const updatedStatus = !user.isEnabled;
      const updatedUsers = [...users];
      updatedUsers[index].isEnabled = updatedStatus;
      setUsers(updatedUsers);

      await updateDoc(userRef, {
        isEnabled: updatedStatus,
      });
    } else {
      console.log("Status change cancelled.");
    }
  };

  const deleteUser = async (index) => {
    const user = users[index];
    const userId = user.userId;

    const confirmed = window.confirm(`Are you sure you want to delete the user "${user.userName}" and all their related data? This action cannot be undone.`);

    if (confirmed) {
      const userRef = doc(db, "users", userId);

      await deleteDoc(userRef);

      const projectsRef = collection(db, "projects");
      const projectsQuery = query(projectsRef, where("userId", "==", userId));
      const projectSnapshot = await getDocs(projectsQuery);
      for (const projectDoc of projectSnapshot.docs) {
        const projectRef = doc(db, "projects", projectDoc.id);
        await deleteDoc(projectRef);

        const assignments = projectDoc.data().assignments || [];
        for (const assignment of assignments) {
          const communications = assignment.communication_list || [];
          for (const communication of communications) {
            const communicationRef = doc(db, "communications", communication.id);
            await deleteDoc(communicationRef);
          }
          const assignmentRef = doc(db, "assignments", assignment.id);
          await deleteDoc(assignmentRef);
        }
      }

      const paymentsRef = collection(db, "payments");
      const paymentsQuery = query(paymentsRef, where("userId", "==", userId));
      const paymentSnapshot = await getDocs(paymentsQuery);
      for (const paymentDoc of paymentSnapshot.docs) {
        const paymentRef = doc(db, "payments", paymentDoc.id);
        await deleteDoc(paymentRef);
      }

      setUsers(users.filter((_, i) => i !== index));
    } else {
      console.log("User deletion cancelled.");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const fetchUnreadNotifications = async () => {
    const notificationsRef = collection(db, "admin", "notifications", "list");
    const notificationsQuery = query(notificationsRef, where("unread", "==", true));
    const notificationsSnapshot = await getDocs(notificationsQuery);
    setUnreadNotificationsCount(notificationsSnapshot.size); 
  };

  const filteredUsers = users.filter((user) => {
    return (
      (filters.name === "" || user.userName.toLowerCase().includes(filters.name.toLowerCase())) &&
      (filters.phone === "" || user.phone.includes(filters.phone)) &&
      (filters.tasks === "" || user.totalAssignments.toString().includes(filters.tasks)) &&
      (filters.payments === "" || user.totalPayments.toString().includes(filters.payments)) &&
      (filters.messages === "" || user.totalCommunications.toString().includes(filters.messages))
    );
  });

  const paginateUsers = () => {
    return filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);
  };

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <Container fluid className="admin-dashboard vh-100 d-flex">
      <Sidebar showSidebar={showSidebar ? "show" : ""} />
      <Container className={`p-2 flex-grow-1 main-content ${showSidebar ? "shifted" : ""}`}>
        <Row>
          <Col>
            <Button variant="dark" onClick={toggleSidebar} className="mb-3">
              <List size={20} />
            </Button>
            <h3>User Management</h3>
            <Link to="/notifications">
              <Button variant="light" className="mb-3" style={{ float: "right", position: "relative" }}>
                <Bell size={20} />
                {unreadNotificationsCount > 0 && (
                  <span 
                    className="badge bg-danger" 
                    style={{
                      position: 'absolute', 
                      top: '0', 
                      right: '0', 
                      fontSize: '12px', 
                      padding: '0.3em', 
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {unreadNotificationsCount}
                  </span>
                )}
              </Button>
            </Link>
            <Form className="mb-3">
              <Row>
                <Col>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Filter by Name"
                    value={filters.name}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    name="phone"
                    placeholder="Filter by Phone"
                    value={filters.phone}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    name="tasks"
                    placeholder="Filter by Tasks"
                    value={filters.tasks}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    name="payments"
                    placeholder="Filter by Payments"
                    value={filters.payments}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    name="messages"
                    placeholder="Filter by Messages"
                    value={filters.messages}
                    onChange={handleFilterChange}
                  />
                </Col>
              </Row>
            </Form>

            <div className="table-responsive overflow-auto" style={{ maxHeight: "60vh" }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                  <th>Sr. No.</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Tasks</th>
                    <th>Payments</th>
                    <th>Messages</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
  {isLoading ? (
    <tr>
      <td colSpan="7">
        <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
          <Spinner animation="border" variant="primary" />
          <span className="ml-2">Loading data...</span>
        </div>
      </td>
    </tr>
  ) : (
    paginateUsers().map((user, index) => (
      <tr key={index}>
        <td>{index+1}</td>
        <td>{user.userName}</td>
        <td>{user.phone}</td>
        <td>{user.totalAssignments}</td>
        <td>{user.totalPayments}</td>
        <td>{user.totalCommunications}</td>
        <td>
          <Form.Check
            type="switch"
            id={`user-status-switch-${index}`}
            label={user.isEnabled ? "Enabled" : "Disabled"}
            checked={user.isEnabled}
            onChange={() => toggleUserStatus(index)}
            custom
          />
        </td>
        <td>
          <Button size="sm" variant="danger" onClick={() => deleteUser(index)}>
            <Trash size={16} />
          </Button>
        </td>
      </tr>
    ))
  )}
</tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              {currentPage > 1 && (
                <Button variant="light" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                  <ChevronLeft /> Previous
                </Button>
              )}
              <span>
                Page {currentPage} of {totalPages}
              </span>
              {currentPage < totalPages && (
                <Button
                  variant="light"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                >
                  Next <ChevronRight />
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default AdminDashboard;












// import React, { useState, useEffect } from "react";
// import { Container, Row, Col, Button, Table } from "react-bootstrap";
// import { ChevronLeft, ChevronRight, List, Trash, EyeSlash, Eye } from "react-bootstrap-icons";
// import { useLocation } from "react-router-dom";
// import { db, collection, doc, getDoc, getDocs, query, where } from ".."; // import your firebase config
// import "/Users/magnaopus/Desktop/invoicely-admin/src/AdminDashboard.css";
// import Sidebar from "./SideBar";

// const AdminDashboard = () => {
//   const location = useLocation();
//   const [showSidebar, setShowSidebar] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const usersPerPage = 10;
//   const [users, setUsers] = useState([]);
  
//   // Fetch data from Firestore
//   useEffect(() => {
//     const fetchData = async () => {
//       // Replace with your collection names
//       const projectsRef = collection(db, 'projects'); 
//       const projectSnapshot = await getDocs(projectsRef);

//       const userData = [];
      
//       for (const projectDoc of projectSnapshot.docs) {
//         const projectId = projectDoc.id;
//         const assignmentsRef = collection(db, 'projects', projectId, 'assignments');
//         const assignmentSnapshot = await getDocs(assignmentsRef);

//         for (const assignmentDoc of assignmentSnapshot.docs) {
//           const assignmentId = assignmentDoc.id;
//           const communicationsRef = collection(db, 'projects', projectId, 'assignments', assignmentId, 'communication_list');
//           const communicationSnapshot = await getDocs(communicationsRef);
          
//           const userId = assignmentDoc.data().userId;
//           const userRef = doc(db, 'users', userId);
//           const userSnapshot = await getDoc(userRef);
          
//           if (userSnapshot.exists()) {
//             const user = userSnapshot.data();
            
//             // Calculate the totals for tasks, messages, and payments
//             const taskCount = assignmentSnapshot.size;
//             const messageCount = communicationSnapshot.size;
//             const paymentCount = Math.floor(Math.random() * 5); // Use your logic for payment count
            
//             userData.push({
//               userId: user.id,
//               userName: user.name,
//               phone: user.phone,
//               tasks: taskCount,
//               messages: messageCount,
//               payments: paymentCount,
//               active: user.isEnabled,
//             });
//           }
//         }
//       }

//       setUsers(userData);
//     };

//     fetchData();
//   }, []);

//   // Ensure sidebar stays open on settings page
//   const toggleSidebar = () => {
//     if (location.pathname !== "/settings") {
//       setShowSidebar(!showSidebar);
//     }
//   };

//   const toggleUserStatus = (index) => {
//     setUsers((prevUsers) =>
//       prevUsers.map((user, i) => (i === index ? { ...user, active: !user.active } : user))
//     );
//   };

//   const deleteUser = (index) => {
//     setUsers(users.filter((_, i) => i !== index));
//   };

//   return (
//     <Container fluid className="admin-dashboard vh-100 d-flex">
//       {/* Sidebar */}
//       <Sidebar showSidebar={showSidebar ? "show" : ""} />
//       {/* Main Content */}
//       <Container className={`p-2 flex-grow-1 main-content ${showSidebar ? "shifted" : ""}`}>
//         <Row>
//           <Col>
//             <Button variant="dark" onClick={toggleSidebar} className="mb-3">
//               <List size={20} />
//             </Button>
//             <h3>User Management</h3>

//             {/* User Table */}
//             <div className="table-responsive overflow-auto" style={{ maxHeight: "60vh" }}>
//               <Table striped bordered hover>
//                 <thead>
//                   <tr>
//                     <th>Name</th>
//                     <th>Phone</th>
//                     <th>Tasks</th>
//                     <th>Messages</th>
//                     <th>Payments</th>
//                     <th>Status</th>
//                     <th>Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {users
//                     .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
//                     .map((user, index) => (
//                       <tr key={index}>
//                         <td>{user.userName}</td>
//                         <td>{user.phone}</td>
//                         <td>{user.tasks}</td>
//                         <td>{user.messages}</td>
//                         <td>{user.payments}</td>
//                         <td>
//                           <Button size="sm" variant={user.active ? "success" : "danger"} onClick={() => toggleUserStatus(index)}>
//                             {user.active ? <Eye size={16} /> : <EyeSlash size={16} />}
//                           </Button>
//                         </td>
//                         <td>
//                           <Button size="sm" variant="danger" onClick={() => deleteUser(index)}>
//                             <Trash size={16} />
//                           </Button>
//                         </td>
//                       </tr>
//                     ))}
//                 </tbody>
//               </Table>
//             </div>

//             {/* Pagination */}
//             <div className="d-flex justify-content-between align-items-center mt-3">
//               <Button variant="light" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
//                 <ChevronLeft /> Previous
//               </Button>
//               <span>
//                 Page {currentPage} of {Math.ceil(users.length / usersPerPage)}
//               </span>
//               <Button
//                 variant="light"
//                 onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(users.length / usersPerPage)))}
//               >
//                 Next <ChevronRight />
//               </Button>
//             </div>
//           </Col>
//         </Row>
//       </Container>
//     </Container>
//   );
// };

// export default AdminDashboard;
